import React from "react";
import { animated, useSpring } from "react-spring";
import MuiAccordion from "@material-ui/core/Accordion";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import clsx from "clsx";

import { ExpandLess as ExpandLessIcon } from "@eroad/icons/material";

import type { CoretexTheme } from "../theme";

const AnimatedIcon = animated(ExpandLessIcon);

const Accordion = withStyles({
  root: {
    boxShadow: "none",
    "&:not(:last-child)": {
      borderBottom: 0,
    },
    "&:before": {
      display: "none",
    },
    "&$expanded": {
      display: "flex",
      flexDirection: "column",
      margin: "0",

      "& >:last-child": {
        flex: 1,

        "& > *": {
          height: "100%",

          "& > *": {
            height: "inherit",

            "& > *": {
              height: "inherit",
            },
          },
        },
      },
    },
  },
  expanded: {},
})(MuiAccordion);

const accordionStyles = makeStyles((theme: CoretexTheme) => ({
  root: {
    backgroundColor: theme.palette.secondary.light,
    minHeight: 32,
    padding: theme.spacing(2),
    "&$expanded": {
      minHeight: 32,
    },
    height: 32,
  },
  content: {
    "&$expanded": {},
  },
  expanded: {},
}));

const useStyles = makeStyles((theme: CoretexTheme) => ({
  expanded: {
    flexGrow: 1,
  },
  heading: {
    fontSize: theme.typography.body2.fontSize,
    fontWeight: theme.typography.fontWeightBold as number,
    color: theme.palette.mono.secondaryDark,
    textTransform: "uppercase",
  },
  icon: {
    color: theme.palette.mono.secondaryDark,
  },
  content: {
    display: "flex",
    flexDirection: "column",
    padding: 0,
    flex: 1,
    height: "100%",
    width: "100%",
  },
}));

const animationPaperConfig = {
  mass: 2,
  friction: 20,
  tension: 120,
  clamp: true,
};

export interface SectionExpandProps {
  children?: React.ReactNode;
  title: string;
  expanded?: boolean;
  onToggle?: (event: React.ChangeEvent<unknown>, isExpanded: boolean) => void;
  toggleInternally?: boolean;
}

const SectionExpand = ({ children, title, expanded, onToggle, toggleInternally }: SectionExpandProps): JSX.Element => {
  const classes = useStyles();

  const [expandAccordion, setExpandAccordion] = React.useState<boolean>(false);
  const accordionOnChange = () => {
    setExpandAccordion(!expandAccordion);
  };

  const isExpanded = expanded || expandAccordion;

  const rotateAnimation = useSpring({
    config: animationPaperConfig,
    transform: isExpanded ? "rotate(180deg)" : "rotate(90deg)",
  });

  return (
    <Accordion
      data-testid={`section-${title}`}
      className={clsx(isExpanded && classes.expanded)}
      square
      expanded={toggleInternally ? expandAccordion : expanded}
      onChange={!toggleInternally ? onToggle : undefined}
    >
      <AccordionSummary
        data-testid="header"
        classes={{
          root: accordionStyles().root,
          content: accordionStyles().content,
          expanded: accordionStyles().expanded,
        }}
        onClick={toggleInternally ? accordionOnChange : undefined}
      >
        <AnimatedIcon style={rotateAnimation} className={classes.icon} />
        <Typography className={classes.heading}>{title}</Typography>
      </AccordionSummary>
      <AccordionDetails data-testid={isExpanded ? "expanded" : "collapsed"} className={classes.content}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
};
export default SectionExpand;
